export const serviceWorkerSource = `/* go-offline service worker */
const DEFAULT_CONFIG = {
  version: "v1",
  offlinePage: "/offline.html",
  offlineImage: null,
  precache: ["/", "/offline.html"],
  runtimeCaching: {
    pages: "network-first",
    images: "cache-first",
    api: "network-first",
    assets: "stale-while-revalidate"
  },
  debug: false
};

let runtimeConfig = { ...DEFAULT_CONFIG };

function log() {
  if (runtimeConfig.debug) {
    console.log("[go-offline/sw]", ...arguments);
  }
}

function cacheName(kind) {
  return "go-offline-" + runtimeConfig.version + "-" + kind;
}

function isNavigationRequest(request) {
  return request.mode === "navigate" || (request.headers.get("accept") || "").includes("text/html");
}

function resourceType(request) {
  const destination = request.destination;
  const url = new URL(request.url);

  if (destination === "image") return "images";
  if (destination === "style" || destination === "script" || destination === "font") return "assets";
  if (isNavigationRequest(request)) return "pages";
  if (url.pathname.startsWith("/api")) return "api";
  return "assets";
}

async function addPrecacheEntries() {
  const cache = await caches.open(cacheName("precache"));
  const entries = Array.from(new Set(runtimeConfig.precache.concat(runtimeConfig.offlinePage)));

  await Promise.all(
    entries.map(async (entry) => {
      try {
        await cache.add(entry);
      } catch (error) {
        log("Failed to precache", entry, error);
      }
    })
  );
}

async function staleWhileRevalidate(request, cacheKind) {
  const cache = await caches.open(cacheName(cacheKind));
  const cached = await cache.match(request);
  const networkPromise = fetch(request)
    .then((response) => {
      if (response && response.ok) {
        cache.put(request, response.clone());
      }
      return response;
    })
    .catch(() => null);

  if (cached) {
    networkPromise.catch(() => null);
    return cached;
  }

  const networkResponse = await networkPromise;
  if (networkResponse) {
    return networkResponse;
  }

  throw new Error("Network request failed and no cache entry exists.");
}

async function cacheFirst(request, cacheKind) {
  const cache = await caches.open(cacheName(cacheKind));
  const cached = await cache.match(request);
  if (cached) {
    return cached;
  }

  const response = await fetch(request);
  if (response && response.ok) {
    cache.put(request, response.clone());
  }
  return response;
}

async function networkFirst(request, cacheKind) {
  const cache = await caches.open(cacheName(cacheKind));

  try {
    const response = await fetch(request);
    if (response && response.ok) {
      cache.put(request, response.clone());
    }
    return response;
  } catch (error) {
    const cached = await cache.match(request);
    if (cached) {
      return cached;
    }
    throw error;
  }
}

async function cacheOnly(request, cacheKind) {
  const cache = await caches.open(cacheName(cacheKind));
  const cached = await cache.match(request);
  if (cached) {
    return cached;
  }

  throw new Error("No cached response available.");
}

function networkOnly(request) {
  return fetch(request);
}

async function offlineFallback(request) {
  if (isNavigationRequest(request)) {
    const precache = await caches.open(cacheName("precache"));
    const page = await precache.match(runtimeConfig.offlinePage);
    if (page) {
      return page;
    }

    return new Response(
      "<!doctype html><html><head><meta charset=\\"utf-8\\"><meta name=\\"viewport\\" content=\\"width=device-width,initial-scale=1\\"><title>Offline</title><style>body{font-family:system-ui,sans-serif;background:#f8fafc;color:#0f172a;display:grid;place-items:center;min-height:100vh;margin:0;padding:24px}.card{max-width:560px;padding:24px;border-radius:20px;background:white;box-shadow:0 20px 50px rgba(15,23,42,.08)}h1{margin-top:0}</style></head><body><div class=\\"card\\"><h1>You are offline</h1><p>This page is not available right now, but the app is still running offline support through go-offline.</p></div></body></html>",
      {
        headers: {
          "Content-Type": "text/html; charset=utf-8"
        }
      }
    );
  }

  if (request.destination === "image" && runtimeConfig.offlineImage) {
    const precache = await caches.open(cacheName("precache"));
    const image = await precache.match(runtimeConfig.offlineImage);
    if (image) {
      return image;
    }
  }

  return Response.error();
}

async function applyStrategy(request) {
  const type = resourceType(request);
  const strategy = runtimeConfig.runtimeCaching[type] || "network-first";

  switch (strategy) {
    case "cache-first":
      return cacheFirst(request, type);
    case "network-first":
      return networkFirst(request, type);
    case "stale-while-revalidate":
      return staleWhileRevalidate(request, type);
    case "cache-only":
      return cacheOnly(request, type);
    case "network-only":
    default:
      return networkOnly(request);
  }
}

self.addEventListener("install", (event) => {
  event.waitUntil(addPrecacheEntries().then(() => self.skipWaiting()));
});

self.addEventListener("activate", (event) => {
  event.waitUntil(
    caches.keys().then(async (keys) => {
      await Promise.all(
        keys
          .filter((key) => key.startsWith("go-offline-") && !key.includes(runtimeConfig.version))
          .map((key) => caches.delete(key))
      );
      await self.clients.claim();
    })
  );
});

self.addEventListener("message", (event) => {
  const data = event.data;
  if (!data || data.type !== "GO_OFFLINE_CONFIG") {
    return;
  }

  runtimeConfig = {
    ...runtimeConfig,
    ...data.config,
    runtimeCaching: {
      ...runtimeConfig.runtimeCaching,
      ...(data.config.runtimeCaching || {})
    }
  };

  event.waitUntil(addPrecacheEntries());
});

self.addEventListener("fetch", (event) => {
  const request = event.request;
  const url = new URL(request.url);

  if (request.method !== "GET") {
    return;
  }

  if (url.origin !== self.location.origin) {
    return;
  }

  event.respondWith(
    applyStrategy(request).catch(() => offlineFallback(request))
  );
});
`;
